import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Switch,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateContact,
  useUpdateContact,
  orgErrorMessage,
  type Contact,
} from '@/api/organization';

interface ContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits this contact; otherwise it creates a new one. */
  contact?: Contact | null;
}

interface FormState {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_position: string;
}

const EMPTY: FormState = {
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  contact_address: '',
  contact_position: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactDialog({ open, onOpenChange, contact }: ContactDialogProps) {
  const { show } = useToast();
  const isEdit = !!contact;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [provisionLogin, setProvisionLogin] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  // Hydrate the form whenever a different contact is opened for editing.
  useEffect(() => {
    if (open) {
      setForm(
        contact
          ? {
              contact_name: contact.contact_name ?? '',
              contact_email: contact.contact_email ?? '',
              contact_phone: contact.contact_phone ?? '',
              contact_address: contact.contact_address ?? '',
              contact_position: contact.contact_position ?? '',
            }
          : EMPTY,
      );
      setProvisionLogin(false);
      setErrors({});
    }
  }, [open, contact]);

  const create = useCreateContact({
    onSuccess: () => {
      show({ tone: 'success', title: 'Contact added' });
      onOpenChange(false);
    },
    onError: (err) =>
      show({ tone: 'danger', title: orgErrorMessage(err, 'Could not add contact') }),
  });

  const update = useUpdateContact(contact?.contact_id ?? 0, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Contact updated' });
      onOpenChange(false);
    },
    onError: (err) =>
      show({ tone: 'danger', title: orgErrorMessage(err, 'Could not update contact') }),
  });

  const pending = create.isPending || update.isPending;

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit() {
    const nextErrors: typeof errors = {};
    if (!form.contact_name.trim()) nextErrors.name = 'Enter a name.';
    if (!form.contact_email.trim()) nextErrors.email = 'Enter an email.';
    else if (!EMAIL_RE.test(form.contact_email.trim())) nextErrors.email = 'Enter a valid email.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const base = {
      contact_name: form.contact_name.trim(),
      contact_email: form.contact_email.trim(),
      contact_phone: form.contact_phone.trim() || undefined,
      contact_address: form.contact_address.trim() || undefined,
      contact_position: form.contact_position.trim() || undefined,
    };

    if (isEdit) update.mutate(base);
    else create.mutate({ ...base, provision_login: provisionLogin });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit contact' : 'Add contact'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the details for this organization contact.'
              : 'Add a person to your organization.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Name" required error={errors.name}>
              {(props) => (
                <Input
                  {...props}
                  value={form.contact_name}
                  invalid={!!errors.name}
                  onChange={(e) => set('contact_name', e.target.value)}
                />
              )}
            </Field>

            <Field label="Email" required error={errors.email}>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={form.contact_email}
                  invalid={!!errors.email}
                  onChange={(e) => set('contact_email', e.target.value)}
                />
              )}
            </Field>

            <Field label="Phone">
              {(props) => (
                <Input
                  {...props}
                  value={form.contact_phone}
                  onChange={(e) => set('contact_phone', e.target.value)}
                />
              )}
            </Field>

            <Field label="Position">
              {(props) => (
                <Input
                  {...props}
                  placeholder="e.g. Operations Lead"
                  value={form.contact_position}
                  onChange={(e) => set('contact_position', e.target.value)}
                />
              )}
            </Field>

            <Field label="Address">
              {(props) => (
                <Input
                  {...props}
                  value={form.contact_address}
                  onChange={(e) => set('contact_address', e.target.value)}
                />
              )}
            </Field>

            {!isEdit && (
              <Field
                label="Provision login"
                description="Create a login for this contact and email them a set-password link."
              >
                {() => (
                  <Switch checked={provisionLogin} onCheckedChange={setProvisionLogin} />
                )}
              </Field>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="contact-form" disabled={pending}>
            {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Add contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
